import React, { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import ICatalog_Item from "../../../models/ICatalog_Item";
import OwButton from "../../shared/OwButton/OwButton";
import { useNavigate } from "react-router-dom";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import IOrder from "../../../models/IOrder";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IAttachment from "../../../models/IAttachment";
import OwS3DownloadLink from "../../shared/OwS3DownloadLink/OwS3DownloadLink";
import { KitBasicListDetail } from "../../order_entry/KitBasicListDetail/KitBasicListDetail";
import AppContext, { useAppContextNonNull } from "@contexts/AppContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import EndpointsEnum from "@models/EndpointsEnum";
import IOnGetOrderDTO from "@models/DTO/IOnGetOrderDTO";
import IOnGetCatalogItemDTO from "@models/DTO/IOnGetCatalogItemDTO";
import IGetCatalogItemDTO from "@models/DTO/IGetCatalogItemDTO";
import { useParams } from "react-router";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IPrice_Group from "@models/IPrice_Group";
import UIService from "@services/UIService";
import { InventoryWidget } from "@components/dashboard/InventoryWidget/InventoryWidget";
import { QtyOnHandValue } from "@components/shared/QtyOnHandValue/QtyOnHandValue";
import { PricePerUnit } from "@components/shared/PricePerUnit/PricePerUnit";
import OrderEntryService from "@services/OrderEntryService";
import UserService from "@services/UserService";
import IPriceDetailUI from "@models/IPriceDetailUI";
import { EcommerceSearchSubHeader } from "@components/order_entry/EcommerceSearchSubHeader/EcommerceSearchSubHeader";
import ICategory from "@models/ICategory";
import { EcommerceCategorySidebar } from "@components/order_entry/EcommerceCategorySidebar/EcommerceCategorySidebar";
import { EcommerceCart } from "../EcommerceCart/EcommerceCart";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";

type Props = {
    isEcommerce?: boolean;
};

export const EcommerceItemDetailPage = (props: Props) => {
    const { supplierModel, userModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const getPriceGroup = () => {
        if (vendorModel?.Price_Group) {
            return vendorModel.Price_Group;
        }
        return undefined;
    };

    const getCatalogGuid = () => {
        if (vendorModel?.Catalog) {
            return vendorModel.Catalog.guid;
        }
    };

    const { sku, guid } = useParams();

    const [item, setItem] = useState<ICatalog_Item | undefined>(undefined);
    const [catalogGuid, setCatalogGuid] = useState<string | undefined>(getCatalogGuid());
    const [priceGroup, setPriceGroup] = useState<IPrice_Group | undefined>(getPriceGroup());
    const [order, setOrder] = useState<IOrder | undefined>(undefined);
    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [retrievedCategories, setRetrievedCategories] = useState<boolean>(false);
    const [cartOpen, setCartOpen] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [enteredSearchTerm, setEnteredSearchTerm] = useState<string>("");
    const [isCommonItems, setIsCommonItems] = useState<boolean>(false);
    const [selectedCategoryGuid, setSelectedCategoryGuid] = useState<string | undefined>(undefined);
    const [qty, setQty] = useState<number>(1);

    const navigate = useNavigate();

    const goBack = () => {
        navigate(-1);
    };

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);
    };

    const onGetCatalogItem = (data: IOnGetCatalogItemDTO) => {
        setItem(data.catalog_item);

        uiContext.setActivityIndicator(false);
    };

    const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setItem(data.catalog_items[0]);

        uiContext.setActivityIndicator(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
        { endpoint: EndpointsEnum.CatalogItemOnGetCatalogItem, fn: onGetCatalogItem },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        uiContext.setActivityIndicator(true);

        // getTypeaheadCatalogItem();
        getCatalogItem();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    useEffect(() => {
        setupSearchSubHeader();
    }, [categories]);

    useEffect(() => {
        if (retrievedCategories) {
            setupSearchSubHeader();
        }
    }, [cartOpen]);

    const getTypeaheadCatalogItem = () => {
        if (catalogGuid && guid && sku) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid,
                name: sku,
                catalogItemGuid: guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            if (priceGroup) {
                payload.priceGroupGuid = priceGroup.guid;
            }

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const getCatalogItem = () => {
        if (catalogGuid && guid) {
            const payload: IGetCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid,
                catalogItemGuid: guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CatalogItemGetCatalogItem, payload);
        }
    };

    const viewCategory = (category: ICategory) => {
        const currentOffsetZero = offset === 0;

        toggleCategorySidebar();

        setIsCommonItems(false);

        setSelectedCategoryGuid(category.guid);

        setEnteredSearchTerm("");

        setOffset(0);

        if (currentOffsetZero) {
            // performSearch("", category.guid);
            navigate(`/ecomm?category=${category.guid}`);
        }

        // move the user to the top of the page
        window.scrollTo(0, 0);
    };

    const toggleCategorySidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const toggleCatalogSidebar = () => {
        if (rootCategories) {
            const component = (
                <EcommerceCategorySidebar
                    rootCategories={rootCategories}
                    toggleSidebar={toggleCategorySidebar}
                    viewCategory={viewCategory}
                    hasNoOrder={true}
                />
            );

            uiContext.setSidebarComponent(component);
            uiContext.toggleSidebar(true, { pullRight: false });
        }
    };

    const toggleCartSidebar = () => {
        setCartOpen(true);
        const cartSidebarComponent = <EcommerceCart socket={socket} />;

        uiContext.setRightSideComponent(cartSidebarComponent);
        uiContext.toggleRightSide(true);
    };

    const cancelSidebar = () => {
        uiContext.setRightSideComponent(null);
        uiContext.toggleRightSide(false);
        setCartOpen(false);
    };

    const performSearch = (searchTerm: string, categoryGuid?: string) => {
        setEnteredSearchTerm(searchTerm);
        setSelectedCategoryGuid(categoryGuid);
        setOffset(0);
        getCatalogItem();

        // we need to switch the user to the search main view page...
    };

    const getCommonItems = () => {
        setIsCommonItems(true);
        setSelectedCategoryGuid(undefined);
        setEnteredSearchTerm("");
        setOffset(0);
        getCatalogItem();
    };

    const viewCartMobile = () => {
        toggleCartSidebar();
    };

    const setupSearchSubHeader = () => {
        if (categories) {
            const subHeaderComponent = (
                <EcommerceSearchSubHeader
                    lookupLeft={"0"}
                    lookupWidth={"100%"}
                    toggleCatalogSidebar={toggleCatalogSidebar}
                    toggleCartSidebar={toggleCartSidebar}
                    cartOpen={cartOpen}
                    closeCartSidebar={cancelSidebar}
                    isReadOnly={false}
                    performSearch={performSearch}
                    isCheckoutView={false}
                    getCommonItems={getCommonItems}
                    viewCart={viewCartMobile}
                    categories={categories}
                    mainUrl={"/ecomm"}
                />
            );

            uiContext.setSubHeaderComponent(subHeaderComponent);
        }
    };

    const getAttachmentDownloadLink = (catalogItem: ICatalog_Item | undefined) => {
        if (catalogItem && catalogItem.Attachments && catalogItem.file_attachment_one_id) {
            let attachmentItem: IAttachment | null = null;

            for (const attachment of catalogItem.Attachments) {
                if (attachment.id === catalogItem.file_attachment_one_id) {
                    attachmentItem = attachment;
                }
            }

            if (attachmentItem) {
                return (
                    <div className={"d-flex"}>
                        <OwS3DownloadLink
                            attachment={attachmentItem}
                            leftIconName={"fad fa-download"}
                            title={"download"}
                            isReadOnly={true}
                        />
                    </div>
                );
            } else {
                return null;
            }
        } else {
            return null;
        }
    };

    const specFileLink = getAttachmentDownloadLink(item);

    const showAddToCart = () => {
        if (item?.has_inventory) {
            if ((item.qty_on_hand || 0) > 0) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    };

    const isLightMode = UIService.isMainViewLight();
    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);
    const itemPrice = item
        ? OrderEntryService.getDiscountedPriceForCatalogItem(item, supplierModel, { Price_Group: priceGroup } as IOrder)
        : ({} as IPriceDetailUI);

    return (
        <div className={"EcommerceItemDetailPage"}>
            <div className={"container mt-4"}>
                <div className="row">
                    <div className="col-12">
                        <OwButton title={"back"} fn={goBack} />
                    </div>
                </div>
                <div className="row mt-4">
                    <div className="col-12 col-md-6">
                        <div className={"card mb-4 " + (isLightMode ? "" : "text-bg-dark")}>
                            <div className="card-body text-center">
                                <div className={"image " + (item?.Attachments?.length ? "has-image" : "")}>
                                    <OwS3ImageViewer
                                        catalogItem={item}
                                        isMicroView={false}
                                        imageIndex={IImageSizeEnum.MEDIUM}
                                        className={"w-15r h-15r"}
                                        includeAllImages={true}
                                        hideEmptyImage={false}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={"card mb-4 " + (isLightMode ? "" : "text-bg-dark")}>
                            <div className="card-header">
                                <OwTitle title={"product_information"} tag={"h5"} />
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6">
                                        <OwLabel title={"sku"} className={"form-label small text-muted"} />
                                        <p className="font-monospace">{item?.sku}</p>
                                    </div>
                                    <div className="col-6">
                                        <OwLabel title={"category"} className={"form-label small text-muted"} />
                                        <p className="">{item?.Category?.name || "N/A"}</p>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <OwLabel title={"inventory"} className={"form-label small text-muted"} />
                                        <p className="">
                                            {item?.has_inventory ? (
                                                <QtyOnHandValue catalogItem={item} />
                                            ) : (
                                                <OwLabel title={"non_inventory_item"} />
                                            )}
                                        </p>
                                    </div>
                                    {!hidePricing ? (
                                        <div className="col-6">
                                            <OwLabel title={"unit_price"} className={"form-label small text-muted"} />
                                            <div className="d-flex">
                                                <div className="me-2">
                                                    <PricePerUnit priceDetail={itemPrice} prefix={"$"} />
                                                </div>
                                                {item?.Unit_Of_Measurement?.abbreviation}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        {item?.description ? (
                            <div className={"card mb-4 " + (isLightMode ? "" : "text-bg-dark")}>
                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-4">
                                            (<div dangerouslySetInnerHTML={{ __html: item?.description || "" }}></div>)
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="col-12 col-md-6">
                        <div className={"card " + (isLightMode ? "" : "text-bg-dark")}>
                            <div className="card-header">
                                <OwTitle title={item?.name || ""} tag={"h3"} disableTranslation={true} />
                                {item?.short_description ? <p className="text-muted">{item?.short_description}</p> : null}
                            </div>
                            <div className="card-body">
                                <div className={"p-3  rounded mb-4 " + (isLightMode ? "bg-light" : "bg-dark")}>
                                    <div className="row g-3 mb-3">
                                        <div className="col-6">
                                            <OwLabel title={"qty"} className={"form-label"} />
                                            <div>
                                                <OwFnInput min={1} type="number" name="qty" fn={() => {}} value={qty} />
                                            </div>
                                        </div>
                                        {!hidePricing ? (
                                            <div className="col-6">
                                                <OwLabel title={"price"} className={"form-label"} />
                                                <div>
                                                    <PricePerUnit priceDetail={itemPrice} prefix={"$"} />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="row">
                                        <div className="col-12">
                                            <OwButton title={"add_to_cart"} fn={() => {}} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {specFileLink || (item?.is_kit && !item?.is_advanced_kit) ? (
                    <div className="row">
                        <div className="col-12">
                            <OwTitle tag={"h4"} title={"additional_details"} />
                            {specFileLink ? (
                                <div>
                                    <OwLabel title={"spec_sheet"} />
                                    <div className={"spec-file"}>{specFileLink}</div>
                                </div>
                            ) : null}
                            {item?.is_kit && !item?.is_advanced_kit ? (
                                <div className={"mt-4"}>
                                    <KitBasicListDetail kitCatalogItem={item} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};
